import React, {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import clsx from "clsx";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Fade from "@material-ui/core/Fade";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";

import { getUploadedFile } from "src/utils/storage";

const useStyles = makeStyles((theme) => ({
  "@keyframes beat": {
    "0%, 100%": {
      backgroundColor: theme.palette.grey[400],
    },
    "50%": {
      backgroundColor: theme.palette.grey[600],
    },
  },
  root: {
    padding: theme.spacing(2),
    height: "auto",
    position: "relative",
  },
  dragActive: {
    backgroundColor: theme.palette.grey[400],
    animation: "$beat 2.5s infinite ease-in-out",
  },
  img: {
    borderRadius: theme.shape.borderRadius,
    maxHeight: 100,
    cursor: "pointer",
    display: "block",
  },
  imgLarge: {
    maxHeight: 160,
  },
  remove: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  placeholder: {
    display: "flex",
    flexDirection: "column",
    justifyItems: "center",
    alignItems: "center",
  },
  hasLogo: {
    minHeight: 100,
  },
  hasLogoLarge: {
    minHeight: 160,
  },
  loading: {
    "& $placeholder": {
      visibility: "hidden",
    },
  },
}));

function loadImage(src: string, retry = 3) {
  const img = new Image();
  return new Promise<void>((rs, rj) => {
    img.addEventListener("load", () => rs());
    img.addEventListener("error", (e) => {
      if (retry < 0) {
        rj(e);
        return;
      }
      loadImage(src, retry - 1)
        .then(rs)
        .catch(rj);
    });
    img.src = src;
  });
}

export const ClearSymbol = Symbol("clear");

export interface ImageInputProps {
  className?: string;
  /** Storage path - will be fetched from Firebase Storage */
  defaultValue?: string;
  /** Ready-to-display URL - skips fetch, takes precedence over defaultValue */
  imageUrl?: string | null;
  onChange?: (file: File | typeof ClearSymbol | null) => void;
  size?: "default" | "large";
  placeholderText?: string;
}

const ImageInput = forwardRef<HTMLDivElement, ImageInputProps>(
  (
    {
      className,
      defaultValue,
      imageUrl,
      onChange,
      size = "default",
      placeholderText = "Drag and drop an image here or click",
    },
    ref
  ) => {
    // Local image state for uploads (data URLs from FileReader)
    const [localImg, setLocalImg] = useState<string | null>(null);
    const refs = useRef({ onChange });
    refs.current.onChange = onChange;

    const onDrop = useCallback((files: File[]) => {
      if (!files[0]) return;
      refs.current.onChange?.(files[0]);
      const reader = new FileReader();
      reader.addEventListener("load", (event) => {
        if (event?.target?.result) setLocalImg(event.target.result as string);
      });
      reader.readAsDataURL(files[0]);
    }, []);

    const onDropRejected = useCallback(() => {
      toast.error("Please upload a valid image file (PNG, JPEG, or WebP)");
    }, []);

    const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
      noClick: true,
      onDrop,
      onDropRejected,
      accept: ["image/png", "image/jpeg", "image/webp"],
    });
    const classes = useStyles();
    const isLarge = size === "large";

    // State for images fetched from storage (only used when imageUrl not provided)
    const [fetchedImg, setFetchedImg] = useState<string | null>(null);

    // Fetch from storage only if imageUrl prop is not provided
    useEffect(() => {
      // If imageUrl is provided, parent handles the URL - clear any stale fetched data
      if (imageUrl !== undefined) {
        setFetchedImg(null);
        return;
      }

      if (!defaultValue) {
        setFetchedImg(null);
        return;
      }

      let cancel = false;
      (async () => {
        try {
          const url = await getUploadedFile(defaultValue);
          if (cancel) return;
          await loadImage(url);
          if (cancel) return;
          setFetchedImg(url);
        } catch (e) {
          if (cancel) return;
          setFetchedImg(null);
        }
      })();

      return () => {
        cancel = true;
      };
    }, [defaultValue, imageUrl]);

    // Priority: local upload > imageUrl prop > fetched from storage
    const img = localImg || imageUrl || fetchedImg;
    const hasValue = defaultValue || imageUrl;

    return (
      <div
        ref={ref}
        className={clsx(
          classes.root,
          className,
          isDragActive && classes.dragActive,
          hasValue && (isLarge ? classes.hasLogoLarge : classes.hasLogo),
          hasValue && !img && classes.loading
        )}
      >
        <div {...getRootProps()}>
          <input {...getInputProps()} />
          {img ? (
            <Fade in={!!img} timeout={500}>
              <div style={{ position: "relative" }}>
                <img
                  className={clsx(classes.img, isLarge && classes.imgLarge)}
                  src={img}
                  alt="Logo"
                  onClick={open}
                />
                <IconButton
                  title="Remove Logo"
                  onClick={(e) => {
                    e.stopPropagation();
                    refs.current.onChange?.(ClearSymbol);
                    setLocalImg(null);
                  }}
                  size="small"
                  className={classes.remove}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              </div>
            </Fade>
          ) : (
            <div onClick={open} className={classes.placeholder}>
              <Typography variant="caption" align="center">
                {placeholderText}
              </Typography>
              <div>
                <IconButton size="small">
                  <CloudUploadIcon fontSize="inherit" />
                </IconButton>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

// Wrapper for use with OutlinedInput's inputComponent prop
export const ImageInputComponent = forwardRef<HTMLDivElement, any>(
  ({ className, inputRef, onChange, defaultValue }, ref) => {
    useImperativeHandle(inputRef, () => ({
      value: true,
      nodeName: "INPUT",
    }));

    return (
      <ImageInput
        ref={ref}
        className={className}
        defaultValue={defaultValue}
        onChange={(value) => {
          onChange?.({ target: { value } });
        }}
      />
    );
  }
);

export default ImageInput;
